import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { Option } from "../../models/Option";
import { Header } from "../../components/shared/Header";
import { Select } from "../../components/shared/Select";
import { Input } from "../../components/shared/inputs/Input";
import { QuillEditor } from "../../components/shared/QuillEditor";
import { Button } from "../../components/shared/Button";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { ResponseModel } from "../../models/ResponseModel";
import { Diploma } from "../../models/Diploma";
import { getStateDiploma, postStateDiplomaEdit } from "../../services/DiplomaService";
import { MenuSelect } from "../../components/shared/MenuSelect";
import { MenuOption } from "../../models/MenuOption";

export const StateDiplomaEditPage: React.FC = () => {   
    const navigate = useNavigate();
    let params = useParams(); 
    const diplomaId: number = +params.diplomaId!;        
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("diploma-edit-page"));
    const authContext = useContext(AuthContext);      
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const [categories, setCategories] = useState<Option[]>([]);
    const [institutes, setInstitutes] = useState<MenuOption[]>([]);
    const [diplomaPeriods, setDiplomaPeriods] = useState<Option[]>([]);
    const [diploma, setDiploma] = useState<Diploma>();
    const [title, setTitle] = useState<string>();
    const [subTitle, setSubTitle] = useState<string>();
    const [loading, setLoading] = useState<boolean>(true); 
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getDiploma();
        }
    }, []);
    
    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("diploma-edit-page")); 
    }, [isMobile]);   

    async function getDiploma() {                
        await getStateDiploma(diplomaId, admin!.adminId, admin!.token)
        .then((data: any) => {
            setCategories(data.categories ?? []);
            setInstitutes(data.institutes ?? []);            
            setDiplomaPeriods(data.diplomaPeriods ?? []);   
            setDiploma(data.diploma ?? []);
            setTitle(data.diploma.stateDiplomaName_SIN);
            setSubTitle(data.diploma.stateDiplomaName_ENG);    
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const handleInstituteChange = (instituteId: number) => {        
        setDiploma((values: any) => ({ ...values, ["stateInstituteId"]: instituteId }));                   
    }

    const handleDiplomaPeriodChange = (diplomaPeriodId: number) => {        
        setDiploma((values: any) => ({ ...values, ["stateDiplomaPeriodId"]: diplomaPeriodId }));              
    }

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        setDiploma((values: any) => ({ ...values, [name]: value }));
    }

    const handleContentChange = (name: string, content: string) => {        
        setDiploma((values: any) => ({ ...values, [name]: content }));
    }

    async function postEditResponse () {            
        await postStateDiplomaEdit(diploma!, admin!.adminId, admin!.token)
        .then((response: ResponseModel) => {                
            setOpenDialog(true);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                               
    }

    const closeDialog = () => {
        setOpenDialog(false);
        navigate('/state-diplomas');        
    }

    return (
        <>
            <Header /> 
            {loading ? <></> : <div style={styles}>
                <div style={styles.titleWrapper}>
                    <h2 style={styles.title}>{title}</h2>
                    <h3 style={styles.title}>{subTitle}</h3>
                </div>                
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Name [SIN]"}</label>
                        <Input
                            type={"text"}
                            name={"stateDiplomaName_SIN"}
                            value={diploma?.stateDiplomaName_SIN}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Name [TAM]"}</label>
                        <Input
                            type={"text"}
                            name={"stateDiplomaName_TAM"}
                            value={diploma?.stateDiplomaName_TAM}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Name [ENG]"}</label>
                        <Input
                            type={"text"}
                            name={"stateDiplomaName_ENG"}
                            value={diploma?.stateDiplomaName_ENG}
                            styles={styles.input}
                            onChange={handleInputChange}                                                                                    
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Categories"}</label>                   
                        <Select 
                            styles={styles.searchSelect}
                            label={"Categories"} 
                            options={categories}                             
                            getSelectedValue={() => {}} 
                        />
                    </div>
                    <div>
                        <label style={styles.label}>{"Institute"}</label>
                        <MenuSelect
                            styles={styles.searchSelect}
                            label={"Institute"} 
                            menuOptions={institutes} 
                            initialValue={diploma?.stateInstituteId!.toString()}
                            getSelectedValue={handleInstituteChange} 
                        />
                    </div>                
                    <div>
                        <label style={styles.label}>{"Diploma Period"}</label>                   
                        <Select 
                            styles={styles.searchSelect}
                            label={"Diploma Period"} 
                            options={diplomaPeriods} 
                            initialValue={diploma?.stateDiplomaPeriodId!.toString()}
                            getSelectedValue={handleDiplomaPeriodChange} 
                        />
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Required Qualifications [SIN]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_SIN"}
                            value={diploma?.requiredQualifications_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Required Qualifications [TAM]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_TAM"}
                            value={diploma?.requiredQualifications_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Required Qualifications[ENG]"}</label>
                        <QuillEditor
                            name={"requiredQualifications_ENG"}
                            value={diploma?.requiredQualifications_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [SIN]"}</label>
                        <QuillEditor
                            name={"stateDiplomaDetail_SIN"}
                            value={diploma?.stateDiplomaDetail_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [TAM]"}</label>
                        <QuillEditor
                            name={"stateDiplomaDetail_TAM"}
                            value={diploma?.stateDiplomaDetail_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Detail [ENG]"}</label>
                        <QuillEditor
                            name={"stateDiplomaDetail_ENG"}
                            value={diploma?.stateDiplomaDetail_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Diploma Content [SIN]"}</label>
                        <QuillEditor
                            name={"stateDiplomaContent_SIN"}
                            value={diploma?.stateDiplomaContent_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diplom Content [TAM]"}</label>
                        <QuillEditor
                            name={"stateDiplomaContent_TAM"}
                            value={diploma?.stateDiplomaContent_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Diploma Content [ENG]"}</label>
                        <QuillEditor
                            name={"stateDiplomaContent_ENG"}
                            value={diploma?.stateDiplomaContent_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.rowWrapper}>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [SIN]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_SIN"}
                            value={diploma?.jobOpportunities_SIN} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [TAM]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_TAM"}
                            value={diploma?.jobOpportunities_TAM} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                    <div>
                        <label style={styles.label}>{"Job Opportunities [ENG]"}</label>
                        <QuillEditor
                            name={"jobOpportunities_ENG"}
                            value={diploma?.jobOpportunities_ENG} 
                            onChange={handleContentChange}
                            styles={styles.editor}
                        /> 
                    </div>
                </div>
                <div style={styles.buttonWrapper}>
                    <Button style={styles.submitButton} onClick={postEditResponse}>{'Submit'}</Button>
                </div>
            </div>}
            <PopUpDialog styles={styles.responseDialog} open={openDialog} toggle={closeDialog}>
                <h3 style={styles.responseTitle}>{"Success"}</h3>
                <p style={styles.responseMessage}>{"The degree details are submitted successfully."}</p>                
                <Button style={styles.responseButton} type="button" onClick={closeDialog}>{"OK"}</Button>    
            </PopUpDialog>
        </>
    )
}